import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Header, Footer } from '../components';
import StarRating from '../components/StarRating';
import { getReviews } from '../lib/api/reviewsApi';

const PAGE_SIZE = 20;

function ReviewsPage() {
  const { t } = useTranslation();
  const [rating, setRating] = useState(null); // null = toutes les notes
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback((selPage, selRating) => {
    setLoading(true);
    getReviews({ page: selPage, pageSize: PAGE_SIZE, rating: selRating || undefined })
      .then(({ data: d }) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(page, rating);
  }, [page, rating, load]);

  const selectRating = (r) => {
    setRating(r);
    setPage(1);
  };

  const counts = data?.ratingCounts || {};
  const totalPages = data?.totalPages || 1;
  const items = data?.items || [];

  return (
    <div className="app-shell">
      <Header />
      <main className="app-main">
        <section className="risks-page">
          <a className="risks-page__back" href="/">{t('reviews.back')}</a>
          <h1 className="risks-page__title">{t('reviews.page_title')}</h1>

          {data && data.count > 0 && (
            <div className="reviews-page__summary">
              <StarRating value={data.average} readOnly size="lg" />
              <span className="reviews__avg">{data.average.toFixed(1)}/5</span>
              <span className="reviews__count">{t('reviews.count', { count: data.count })}</span>
            </div>
          )}

          <div className="reviews-page__filters">
            <button
              type="button"
              className={`reviews-filter ${rating === null ? 'is-active' : ''}`}
              onClick={() => selectRating(null)}
            >
              {t('reviews.filter_all')} ({data?.count ?? 0})
            </button>
            {[5, 4, 3, 2, 1].map((r) => (
              <button
                type="button"
                key={r}
                className={`reviews-filter ${rating === r ? 'is-active' : ''}`}
                onClick={() => selectRating(r)}
              >
                {r}★ ({counts[r] ?? 0})
              </button>
            ))}
          </div>

          {loading ? (
            <p className="risks-page__empty">…</p>
          ) : items.length === 0 ? (
            <p className="risks-page__empty">{t('reviews.none')}</p>
          ) : (
            <>
              <ul className="reviews-page__list">
                {items.map((r) => (
                  <li key={r.id} className="reviews-page__item">
                    <div className="reviews-page__item-head">
                      <StarRating value={r.rating} readOnly size="sm" />
                      <span className="reviews-page__author">{r.authorName || t('reviews.anon')}</span>
                      <span className="reviews-page__date">
                        {new Date(r.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    {r.comment && <p className="reviews-page__text">“{r.comment}”</p>}
                  </li>
                ))}
              </ul>

              {totalPages > 1 && (
                <div className="reviews-page__pager">
                  <button
                    type="button"
                    className="btn btn--ghost"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    ← {t('reviews.prev')}
                  </button>
                  <span className="reviews-page__pageinfo">{page} / {totalPages}</span>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    {t('reviews.next')} →
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default ReviewsPage;
